import React, { useState, useEffect } from 'react';

interface Recipient {
  id: string;
  name: string;
  phone: string;
  type: 'family-primary' | 'family-secondary' | 'alumni';
  category?: string;
  familyName?: string;
  optedIn: boolean;
}

interface FamilyRecipient {
  id: string;
  name: string;
  primaryContact: {
    name: string;
    phone: string;
    optedIn: boolean;
  };
  secondaryContact?: {
    name: string;
    phone: string;
    optedIn: boolean;
  } | null;
  ranchGirls: Array<{
    id: string;
    name: string;
  }>;
}

interface AlumniRecipient {
  id: string;
  name: string;
  category: string;
  phone: string;
  optedIn: boolean;
}

interface SendProgress {
  recipient: Recipient;
  status: 'pending' | 'sending' | 'sent' | 'failed';
  error?: string;
}

interface AuditLogEntry {
  id: number;
  createdAt: string;
  endpoint: string;
  adminEmail: string | null;
  targetUserEmail: string | null;
  sourceIp: string | null;
  recipientPhone: string | null;
  recipientName: string | null;
  messagePreview: string | null;
  success: boolean;
  blockedByCap: boolean;
  errorMessage: string | null;
  quotaAfter: number | null;
}

interface AuditLogData {
  cap: number;
  sentLast24h: number;
  sentLast7d: number;
  blockedLast24h: number;
  totalAllTime: number;
  capRemaining: number;
  entries: AuditLogEntry[];
}

export default function TextMessaging() {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [families, setFamilies] = useState<FamilyRecipient[]>([]);
  const [alumni, setAlumni] = useState<AlumniRecipient[]>([]);
  const [message, setMessage] = useState('');
  const [selectedRecipients, setSelectedRecipients] = useState<Recipient[]>([]);
  const [selectionMode, setSelectionMode] = useState<'all' | 'specific'>('all');
  const [filterType, setFilterType] = useState<'all' | 'families' | 'alumni'>('all');
  const [quotaRemaining, setQuotaRemaining] = useState<number | string | null>(null);
  const [testPhone, setTestPhone] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [sendResult, setSendResult] = useState<any>(null);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [sendProgress, setSendProgress] = useState<SendProgress[]>([]);
  const [activeTab, setActiveTab] = useState<'send' | 'activity'>('send');
  const [auditData, setAuditData] = useState<AuditLogData | null>(null);
  const [loadingAudit, setLoadingAudit] = useState(false);

  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('authToken');
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {})
    };

    const response = await fetch(url, {
      ...options,
      headers
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return await response.json();
  };

  useEffect(() => {
    fetchRecipients();
    checkQuota();
  }, []);

  const fetchRecipients = async () => {
    try {
      setLoading(true);
      const data = await authenticatedFetch('/api/textmessages/recipients');
      if (data.success) {
        setFamilies(data.data.families);
        setAlumni(data.data.alumni);
      }
    } catch (error) {
      console.error('Error fetching recipients:', error);
      alert('Failed to load recipients');
    } finally {
      setLoading(false);
    }
  };

  const checkQuota = async () => {
    try {
      const data = await authenticatedFetch('/api/textmessages/quota');
      if (data.success) {
        setQuotaRemaining(data.data.quotaRemaining);
      }
    } catch (error) {
      console.error('Error checking ReadySMS balance:', error);
    }
  };

  const sendTestSms = async () => {
    const phone = testPhone.trim();
    if (!phone) {
      setTestResult({ success: false, message: 'Enter a phone number to test.' });
      return;
    }

    try {
      setTestSending(true);
      setTestResult(null);
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/textmessages/send-test', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ phone })
      });
      const data = await response.json();
      if (data.success) {
        setTestResult({
          success: true,
          message: data.message || 'Test SMS sent — check your phone.'
        });
        checkQuota();
      } else {
        setTestResult({
          success: false,
          message: data.message || 'Test send failed.'
        });
      }
    } catch (error: any) {
      setTestResult({
        success: false,
        message: error.message || 'Test send failed.'
      });
    } finally {
      setTestSending(false);
    }
  };

  const fetchAuditLog = async () => {
    try {
      setLoadingAudit(true);
      const data = await authenticatedFetch('/api/textmessages/audit-log?limit=200');
      if (data.success) {
        setAuditData(data.data);
      }
    } catch (error) {
      console.error('Error fetching SMS audit log:', error);
    } finally {
      setLoadingAudit(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'activity') {
      fetchAuditLog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const formatTimestamp = (iso: string) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString();
    } catch {
      return iso;
    }
  };

  const formatPhone = (phone: string | null) => {
    if (!phone) return '—';
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return phone;
  };

  const getAllRecipients = (): Recipient[] => {
    const recipients: Recipient[] = [];

    // Add family contacts who have opted in
    families.forEach(family => {
      if (family.primaryContact.optedIn) {
        recipients.push({
          id: `family-primary-${family.id}`,
          name: family.primaryContact.name,
          phone: family.primaryContact.phone,
          type: 'family-primary',
          familyName: family.name,
          optedIn: true
        });
      }
      if (family.secondaryContact && family.secondaryContact.optedIn) {
        recipients.push({
          id: `family-secondary-${family.id}`,
          name: family.secondaryContact.name,
          phone: family.secondaryContact.phone,
          type: 'family-secondary',
          familyName: family.name,
          optedIn: true
        });
      }
    });

    // Add alumni who have opted in
    alumni.forEach(member => {
      if (member.optedIn && member.phone) {
        recipients.push({
          id: `alumni-${member.id}`,
          name: member.name,
          phone: member.phone,
          type: 'alumni',
          category: member.category,
          optedIn: true
        });
      }
    });

    return recipients;
  };

  const getFilteredRecipients = (): Recipient[] => {
    const all = getAllRecipients();
    
    if (filterType === 'all') {
      return all;
    } else if (filterType === 'families') {
      return all.filter(r => r.type.startsWith('family'));
    } else if (filterType === 'alumni') {
      return all.filter(r => r.type === 'alumni');
    }
    
    return all;
  };

  const handleSelectAll = () => {
    const filtered = getFilteredRecipients();
    setSelectedRecipients(filtered);
    setSelectionMode('all');
  };

  const handleToggleRecipient = (recipient: Recipient) => {
    setSelectionMode('specific');
    const isSelected = selectedRecipients.some(r => r.id === recipient.id);
    
    if (isSelected) {
      setSelectedRecipients(selectedRecipients.filter(r => r.id !== recipient.id));
    } else {
      setSelectedRecipients([...selectedRecipients, recipient]);
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim()) {
      alert('Please enter a message');
      return;
    }

    if (selectedRecipients.length === 0) {
      alert('Please select at least one recipient');
      return;
    }

    const recipientsWithoutPhone = selectedRecipients.filter(r => !r.phone || !r.phone.trim());
    if (recipientsWithoutPhone.length > 0) {
      alert(`Warning: ${recipientsWithoutPhone.length} recipient(s) do not have phone numbers and will be skipped.`);
    }

    if (!window.confirm(`Send text message to ${selectedRecipients.length} recipient(s)?\n\nMessages will be sent at 3 second intervals.`)) {
      return;
    }

    try {
      setSending(true);
      
      // Initialize progress tracking
      const initialProgress: SendProgress[] = selectedRecipients.map(recipient => ({
        recipient,
        status: 'pending' as const
      }));
      setSendProgress(initialProgress);
      setShowProgressModal(true);

      // Send messages one at a time with 1.5 second delay
      const results: SendProgress[] = [];
      
      for (let i = 0; i < selectedRecipients.length; i++) {
        const recipient = selectedRecipients[i];
        
        // Update status to sending
        setSendProgress(prev => 
          prev.map((p, idx) => 
            idx === i ? { ...p, status: 'sending' as const } : p
          )
        );

        try {
          const data = await authenticatedFetch('/api/textmessages/send-single', {
            method: 'POST',
            body: JSON.stringify({
              message: message,
              recipient: recipient
            })
          });

          // Update status based on result
          if (data.success) {
            setSendProgress(prev => 
              prev.map((p, idx) => 
                idx === i ? { ...p, status: 'sent' as const } : p
              )
            );
            results.push({ recipient, status: 'sent' });
          } else {
            setSendProgress(prev => 
              prev.map((p, idx) => 
                idx === i ? { ...p, status: 'failed' as const, error: data.error || 'Unknown error' } : p
              )
            );
            results.push({ recipient, status: 'failed', error: data.error || 'Unknown error' });
          }
        } catch (error: any) {
          setSendProgress(prev => 
            prev.map((p, idx) => 
              idx === i ? { ...p, status: 'failed' as const, error: error.message } : p
            )
          );
          results.push({ recipient, status: 'failed', error: error.message });
        }

        // Wait 3 seconds before sending the next message (unless it's the last one)
        if (i < selectedRecipients.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 3000));
        }
      }

      // Prepare final results
      const sent = results.filter(r => r.status === 'sent').length;
      const failed = results.filter(r => r.status === 'failed').length;
      const errors = results.filter(r => r.status === 'failed').map(r => ({
        name: r.recipient.name,
        phone: r.recipient.phone,
        error: r.error || 'Unknown error'
      }));

      setSendResult({
        sent,
        failed,
        results: results.filter(r => r.status === 'sent').map(r => ({
          name: r.recipient.name,
          phone: r.recipient.phone,
          status: 'sent'
        })),
        errors
      });

      checkQuota(); // Refresh quota
      
      // Don't close modal or clear form - let user review results
    } catch (error) {
      console.error('Error sending messages:', error);
      alert('Failed to send messages. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const optedInCount = getAllRecipients().length;
  const familyOptedIn = families.reduce((count, f) => {
    return count + (f.primaryContact.optedIn ? 1 : 0) + (f.secondaryContact?.optedIn ? 1 : 0);
  }, 0);
  const alumniOptedIn = alumni.filter(a => a.optedIn && a.phone).length;

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ggaa-green"></div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-12 py-6 lg:py-10">
      <div className="mb-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">📱 Text Messaging</h2>
        <p className="text-gray-600 mt-1">Send text notifications to members and alumni</p>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-6">
          <button
            onClick={() => setActiveTab('send')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'send'
                ? 'border-ggaa-green text-ggaa-green'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Send Messages
          </button>
          <button
            onClick={() => setActiveTab('activity')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'activity'
                ? 'border-ggaa-green text-ggaa-green'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Activity Log
          </button>
        </nav>
      </div>

      {activeTab === 'activity' && (
        <ActivityLogView
          data={auditData}
          loading={loadingAudit}
          onRefresh={fetchAuditLog}
          formatTimestamp={formatTimestamp}
          formatPhone={formatPhone}
        />
      )}

      {activeTab === 'send' && (
      <>
      {/* ReadySMS status + test send */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-blue-900">ReadySMS</h3>
            {quotaRemaining !== null && (
              <p className="text-blue-700 mt-1">
                Account credits: <span className="font-bold">{quotaRemaining}</span>
              </p>
            )}
            <p className="text-blue-600 text-sm mt-1">
              Send a test before blasting a group. Uses one message credit.
            </p>
          </div>
          <button
            onClick={checkQuota}
            className="text-blue-600 hover:text-blue-800 underline text-sm self-start"
          >
            Refresh balance
          </button>
        </div>

        <div className="mt-4 flex flex-col sm:flex-row gap-3">
          <input
            type="tel"
            value={testPhone}
            onChange={(e) => setTestPhone(e.target.value)}
            placeholder="Your phone (10 digits)"
            className="flex-1 border border-blue-200 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green bg-white"
          />
          <button
            onClick={sendTestSms}
            disabled={testSending}
            className="px-5 py-2 bg-ggaa-green text-white rounded-lg font-medium hover:bg-ggaa-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {testSending ? 'Sending…' : 'Send Test SMS'}
          </button>
        </div>

        {testResult && (
          <p
            className={`mt-3 text-sm font-medium ${
              testResult.success ? 'text-green-700' : 'text-red-700'
            }`}
          >
            {testResult.message}
          </p>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <h3 className="text-sm font-medium text-gray-600">Total Opted In</h3>
          <p className="text-2xl font-bold text-ggaa-green mt-1">{optedInCount}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <h3 className="text-sm font-medium text-gray-600">Family Contacts</h3>
          <p className="text-2xl font-bold text-blue-600 mt-1">{familyOptedIn}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <h3 className="text-sm font-medium text-gray-600">Alumni Members</h3>
          <p className="text-2xl font-bold text-purple-600 mt-1">{alumniOptedIn}</p>
        </div>
      </div>

      {/* Message Composer */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 mb-6">
        <div className="bg-gradient-to-r from-ggaa-green to-ggaa-dark px-6 py-4">
          <h3 className="text-xl font-semibold text-white">Compose Message</h3>
        </div>
        <div className="p-6">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message <span className="text-red-500">*</span>
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
              placeholder="Enter your message here..."
            />
            <p className="text-sm text-gray-500 mt-1">
              Character count: {message.length} {message.length > 160 && '(May be sent as multiple messages)'}
            </p>
          </div>
        </div>
      </div>

      {/* Recipient Selection */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200">
        <div className="bg-gradient-to-r from-ggaa-brown to-ggaa-brown/80 px-6 py-4">
          <h3 className="text-xl font-semibold text-white">Select Recipients</h3>
        </div>
        <div className="p-6">
          {/* Filter Options */}
          <div className="mb-4 flex flex-wrap gap-3">
            <button
              onClick={() => {
                setFilterType('all');
                if (selectionMode === 'all') {
                  handleSelectAll();
                }
              }}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterType === 'all'
                  ? 'bg-ggaa-green text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              All ({optedInCount})
            </button>
            <button
              onClick={() => {
                setFilterType('families');
                if (selectionMode === 'all') {
                  setSelectedRecipients(getFilteredRecipients().filter(r => r.type.startsWith('family')));
                }
              }}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterType === 'families'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Families ({familyOptedIn})
            </button>
            <button
              onClick={() => {
                setFilterType('alumni');
                if (selectionMode === 'all') {
                  setSelectedRecipients(getFilteredRecipients().filter(r => r.type === 'alumni'));
                }
              }}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterType === 'alumni'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Alumni ({alumniOptedIn})
            </button>
          </div>

          {/* Select All Button */}
          <div className="mb-4">
            <button
              onClick={handleSelectAll}
              className="bg-ggaa-green text-white px-6 py-2 rounded-lg hover:bg-ggaa-dark transition-colors font-medium"
            >
              Select All {filterType !== 'all' && filterType.charAt(0).toUpperCase() + filterType.slice(1)} ({getFilteredRecipients().length})
            </button>
            {selectedRecipients.length > 0 && (
              <span className="ml-4 text-gray-700">
                <strong>{selectedRecipients.length}</strong> selected
              </span>
            )}
          </div>

          {/* Recipients List */}
          <div className="border border-gray-300 rounded-lg max-h-96 overflow-y-auto">
            {getFilteredRecipients().length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <p className="text-lg mb-2">No recipients have opted in for text notifications</p>
                <p className="text-sm">Recipients must opt in on their profile pages to receive text messages.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {getFilteredRecipients().map(recipient => (
                  <label
                    key={recipient.id}
                    className="flex items-center p-4 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedRecipients.some(r => r.id === recipient.id)}
                      onChange={() => handleToggleRecipient(recipient)}
                      className="h-4 w-4 text-ggaa-green focus:ring-ggaa-green border-gray-300 rounded"
                    />
                    <div className="ml-3 flex-1">
                      <div className="font-medium text-gray-900">{recipient.name}</div>
                      <div className="text-sm text-gray-600">
                        {recipient.phone}
                        {recipient.familyName && ` • ${recipient.familyName}`}
                        {recipient.category && ` • ${recipient.category}`}
                      </div>
                    </div>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      recipient.type.startsWith('family')
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-purple-100 text-purple-800'
                    }`}>
                      {recipient.type === 'family-primary' ? 'Family Primary' :
                       recipient.type === 'family-secondary' ? 'Family Secondary' :
                       'Alumni'}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Send Button */}
        <div className="bg-gray-50 px-6 py-4 flex justify-end">
          <button
            onClick={handleSendMessage}
            disabled={sending || selectedRecipients.length === 0 || !message.trim()}
            className="bg-ggaa-green text-white px-8 py-3 rounded-lg hover:bg-ggaa-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-semibold text-lg shadow-md"
          >
            {sending ? '📤 Sending...' : `📤 Send to ${selectedRecipients.length} Recipient${selectedRecipients.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>

      {/* Send Result */}
      {sendResult && (
        <div className="mt-6 bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">📊 Send Results</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="font-medium text-green-900">✅ Successfully Sent</h4>
              <p className="text-2xl font-bold text-green-700 mt-1">{sendResult.sent}</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h4 className="font-medium text-red-900">❌ Failed</h4>
              <p className="text-2xl font-bold text-red-700 mt-1">{sendResult.failed}</p>
            </div>
          </div>

          {sendResult.errors && sendResult.errors.length > 0 && (
            <div className="mt-4">
              <h4 className="font-medium text-red-900 mb-2">Failed Recipients:</h4>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-h-48 overflow-y-auto">
                {sendResult.errors.map((error: any, index: number) => (
                  <div key={index} className="text-sm text-red-800 mb-2">
                    • {error.name} ({error.phone}): {error.error}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Important Notice */}
      <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h4 className="font-semibold text-yellow-900 mb-2">⚠️ Important Notes</h4>
        <ul className="list-disc list-inside text-yellow-800 text-sm space-y-1">
          <li>Only recipients who have opted in will receive messages</li>
          <li>Recipients can opt in/out on their profile pages</li>
          <li>Messages over 160 characters may be sent as multiple texts</li>
          <li>Standard SMS rates may apply to recipients</li>
          <li>Messages are sent at 3 second intervals to avoid rate limits</li>
        </ul>
      </div>
      </>
      )}

      {/* Progress Modal */}
      {showProgressModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-ggaa-green to-ggaa-dark px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-semibold text-white">
                📤 Sending Text Messages
              </h3>
              {!sending && (
                <button
                  onClick={() => {
                    setShowProgressModal(false);
                    setSendProgress([]);
                  }}
                  className="text-white hover:text-gray-200 text-2xl leading-none"
                >
                  ×
                </button>
              )}
            </div>

            {/* Progress Stats */}
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {sendProgress.filter(p => p.status === 'sending').length}
                  </div>
                  <div className="text-xs text-gray-600">Sending</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {sendProgress.filter(p => p.status === 'sent').length}
                  </div>
                  <div className="text-xs text-gray-600">Sent</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">
                    {sendProgress.filter(p => p.status === 'failed').length}
                  </div>
                  <div className="text-xs text-gray-600">Failed</div>
                </div>
              </div>
              
              {/* Progress Bar */}
              <div className="mt-4">
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-ggaa-green to-ggaa-dark h-full transition-all duration-300 ease-out"
                    style={{
                      width: `${((sendProgress.filter(p => p.status === 'sent' || p.status === 'failed').length) / sendProgress.length) * 100}%`
                    }}
                  />
                </div>
                <div className="text-center text-sm text-gray-600 mt-2">
                  {sendProgress.filter(p => p.status === 'sent' || p.status === 'failed').length} of {sendProgress.length} completed
                </div>
              </div>
            </div>

            {/* Recipients List */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="space-y-2">
                {sendProgress.map((progress, index) => (
                  <div
                    key={progress.recipient.id}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      progress.status === 'sent'
                        ? 'bg-green-50 border-green-200'
                        : progress.status === 'failed'
                        ? 'bg-red-50 border-red-200'
                        : progress.status === 'sending'
                        ? 'bg-blue-50 border-blue-200'
                        : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">
                        {progress.recipient.name}
                      </div>
                      <div className="text-sm text-gray-600 truncate">
                        {progress.recipient.phone}
                      </div>
                      {progress.error && (
                        <div className="text-xs text-red-600 mt-1">
                          Error: {progress.error}
                        </div>
                      )}
                    </div>
                    <div className="ml-4 flex-shrink-0">
                      {progress.status === 'pending' && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-700">
                          ⏳ Pending
                        </span>
                      )}
                      {progress.status === 'sending' && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-200 text-blue-800">
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-800 mr-2"></div>
                          Sending...
                        </span>
                      )}
                      {progress.status === 'sent' && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-200 text-green-800">
                          ✓ Sent
                        </span>
                      )}
                      {progress.status === 'failed' && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-200 text-red-800">
                          ✗ Failed
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end">
              {sending ? (
                <div className="text-gray-600 flex items-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-ggaa-green mr-2"></div>
                  Sending messages... Please wait
                </div>
              ) : (
                <button
                  onClick={() => {
                    setShowProgressModal(false);
                    setSendProgress([]);
                    setMessage('');
                    setSelectedRecipients([]);
                  }}
                  className="bg-ggaa-green text-white px-6 py-2 rounded-lg hover:bg-ggaa-dark transition-colors font-medium"
                >
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface ActivityLogViewProps {
  data: AuditLogData | null;
  loading: boolean;
  onRefresh: () => void;
  formatTimestamp: (iso: string) => string;
  formatPhone: (phone: string | null) => string;
}

function ActivityLogView({ data, loading, onRefresh, formatTimestamp, formatPhone }: ActivityLogViewProps) {
  if (loading && !data) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-ggaa-green"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-600">
        No audit data available.
        <div className="mt-4">
          <button
            onClick={onRefresh}
            className="bg-ggaa-green text-white px-4 py-2 rounded-lg hover:bg-ggaa-dark transition-colors text-sm font-medium"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  const capPercent = data.cap > 0 ? Math.min(100, Math.round((data.sentLast24h / data.cap) * 100)) : 0;
  const capBarColor =
    capPercent >= 90 ? 'bg-red-500' : capPercent >= 70 ? 'bg-yellow-500' : 'bg-ggaa-green';

  return (
    <div>
      {/* Daily cap status */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 mb-6 p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900">Daily SMS Cap (last 24h)</h3>
          <button
            onClick={onRefresh}
            disabled={loading}
            className="text-blue-600 hover:text-blue-800 underline text-sm disabled:opacity-50"
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
        <div className="flex items-baseline space-x-2 mb-2">
          <span className="text-3xl font-bold text-gray-900">{data.sentLast24h}</span>
          <span className="text-gray-500">/ {data.cap} sent</span>
          <span className="ml-auto text-sm text-gray-600">{data.capRemaining} remaining</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
          <div
            className={`${capBarColor} h-full transition-all duration-300`}
            style={{ width: `${capPercent}%` }}
          />
        </div>
        {data.blockedLast24h > 0 && (
          <p className="mt-3 text-sm text-red-700">
            ⚠️ {data.blockedLast24h} send attempt{data.blockedLast24h !== 1 ? 's were' : ' was'} blocked by the daily cap in the last 24 hours.
          </p>
        )}
        <p className="mt-3 text-xs text-gray-500">
          Cap is configured by the <code className="px-1 py-0.5 bg-gray-100 rounded">SMS_DAILY_CAP</code> environment variable on the server.
        </p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <h4 className="text-xs font-medium text-gray-600 uppercase tracking-wider">Sent (24h)</h4>
          <p className="text-2xl font-bold text-ggaa-green mt-1">{data.sentLast24h}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <h4 className="text-xs font-medium text-gray-600 uppercase tracking-wider">Sent (7d)</h4>
          <p className="text-2xl font-bold text-blue-600 mt-1">{data.sentLast7d}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <h4 className="text-xs font-medium text-gray-600 uppercase tracking-wider">Blocked (24h)</h4>
          <p className="text-2xl font-bold text-red-600 mt-1">{data.blockedLast24h}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <h4 className="text-xs font-medium text-gray-600 uppercase tracking-wider">All-time entries</h4>
          <p className="text-2xl font-bold text-gray-700 mt-1">{data.totalAllTime}</p>
        </div>
      </div>

      {/* Recent activity */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200">
        <div className="bg-gradient-to-r from-ggaa-brown to-ggaa-brown/80 px-6 py-4">
          <h3 className="text-xl font-semibold text-white">Recent SMS Activity</h3>
          <p className="text-white/80 text-sm">Every SMS attempt – successes, failures, and blocked sends.</p>
        </div>
        {data.entries.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No SMS activity recorded yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">When</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Endpoint</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Triggered by</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Source IP</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Recipient</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Detail</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data.entries.map((entry) => {
                  let statusBadge;
                  if (entry.blockedByCap) {
                    statusBadge = (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        🚧 Blocked
                      </span>
                    );
                  } else if (entry.success) {
                    statusBadge = (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        ✓ Sent
                      </span>
                    );
                  } else {
                    statusBadge = (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        ✗ Failed
                      </span>
                    );
                  }

                  const triggeredBy = entry.adminEmail || entry.targetUserEmail || '—';
                  const recipient = entry.recipientName
                    ? `${entry.recipientName} (${formatPhone(entry.recipientPhone)})`
                    : formatPhone(entry.recipientPhone);

                  return (
                    <tr key={entry.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-xs text-gray-700 whitespace-nowrap">
                        {formatTimestamp(entry.createdAt)}
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-700 whitespace-nowrap font-mono">
                        {entry.endpoint}
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-700 break-all">
                        {triggeredBy}
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-500 font-mono whitespace-nowrap">
                        {entry.sourceIp || '—'}
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-700 break-all">
                        {recipient}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {statusBadge}
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-600 max-w-xs">
                        {entry.errorMessage ? (
                          <span className="text-red-700">{entry.errorMessage}</span>
                        ) : entry.messagePreview ? (
                          <span className="text-gray-600 italic">"{entry.messagePreview.slice(0, 60)}{entry.messagePreview.length > 60 ? '…' : ''}"</span>
                        ) : '—'}
                        {typeof entry.quotaAfter === 'number' && (
                          <div className="text-gray-400 mt-0.5">credits after: {entry.quotaAfter}</div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

